import { Injectable, signal } from '@angular/core';

export interface PassportDetails {
  id: string;
  loadNumber?: string;
  dateTime?: string; // ISO 8601
  dueDateTime?: string; // ISO 8601 - scheduled/due date from backend
  plannedDateTime?: string; // ISO 8601 - planned date from backend
  variety?: string;
  vendorName?: string;
  vendorNumber?: string;
  apn?: string; // APN code (e.g., "1006")
  apnName?: string; // APN name (e.g., "Greenfield Estate")
  haulier?: string;
  destination?: string;
  destinationWeighbridge?: string; // Weighbridge destination label from backend
  origin?: string;
  loadingDateTime?: string; // ISO
  bulkerNumber?: string;
  bulkerMain?: string; // Scheduled bulker from Passport.Passport.TrailerNumber (trailerMain)
  haccpCheck?: 'Yes' | 'No';
  // Legacy (Grower default) - kept for backward compatibility
  haccpImages?: string[];
  haccpImageLinks?: string[];
  haccpImageFiles?: File[];
  deletedImageFilenames?: string[];
  imageCount?: number; // Image count from backend (for showing toast when images fail to load)

  // Role-specific HACCP media
  roleHaccp?: {
    [role in 'Grower' | 'Transloader' | 'Receiver' | 'Shunting' | 'Scheduler' | 'Gatehouse']?: {
      images?: string[];          // display list
      imageLinks?: string[];      // backend links
      imageFiles?: File[];        // newly added local files
      deletedFilenames?: string[];// backend blob paths to delete
    }
  };
  authorisedBy?: string;
  authorisedByUserID?: number; // Authorizer user ID from backend - used to preserve ID when Scheduler submits
  ownerUserID?: number; // The ID of the passport owner (usually Grower) - used to load correct authorizers for Scheduler viewing Grower load
  plantID?: number; // Plant ID from backend
  createdOn?: string; // Created timestamp from backend (ISO format)
  loadStatusName?: string; // Load status name (e.g., "Cancelled")
  comments?: any[]; // Comments from offline storage
  history?: any[]; // History from offline storage
  isCancelled?: boolean; // Whether the passport is cancelled
  hasSeenPcrChanges?: boolean; // Whether there are unseen PCR changes (PC&R flag)
  statusCode?: string; // Status code from backend (e.g., "AWAITING_GROWER_ACTION")
  shuntingYardID?: number | null; // Shunting Yard ID from backend
  shuntingYardName?: string; // Shunting Yard Name from backend
  isShuntingRequired?: boolean; // Whether shunting yard selection is enabled for this plant
  isPassportCompleted?: boolean; // Whether both Grower and Transloader have completed their sections
  /** Receiver persona: receiving workflow marked complete (distinct from grower/transloader completion). */
  isReceivingCompleted?: boolean;
  transloadingStationID?: number | null; // Transloading Station ID from backend (can be null)
  growerAccountID?: number | null; // Grower Account ID from backend (1-to-many mapping)
  originGroupKey?: string; // Group key for shared origins: regionCode|seasonCode|vendorNumber|varietyGUID|certificationGUID
  transferFlag?: boolean;
}

@Injectable({ providedIn: 'root' })
export class PassportDetailsStore {
  private readonly byId = signal<Record<string, PassportDetails>>({});

  get(id: string): PassportDetails | undefined {
    return this.byId()[id];
  }

  upsert(record: PassportDetails): void {
    const map = { ...this.byId() };
    map[record.id] = { ...map[record.id], ...record };
    this.byId.set(map);
  }

  update(id: string, update: Partial<PassportDetails>): void {
    const map = { ...this.byId() };
    map[id] = { ...(map[id] || { id }), ...update };
    this.byId.set(map);
  }

  mutate(id: string, fn: (r: PassportDetails) => PassportDetails): void {
    const current = this.get(id) || { id };
    this.upsert(fn(current));
  }

  clear(): void {
    this.byId.set({});
  }
}


